import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #111;
  padding-bottom: 100px;
`;

const Article = styled.article`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;
`;

// ─── Back Link ────────────────────────────────────────────────────────────────

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #fe88f1;
  text-decoration: none;
  margin-top: 48px;
  margin-bottom: 32px;
  transition: gap 0.2s ease;

  &::before {
    content: '←';
  }

  &:hover {
    gap: 10px;
  }
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const PageHeader = styled.div`
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 40px;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  background: #fe88f1;
  color: #111;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  margin-bottom: 18px;
`;

const PageTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.25;
  margin: 0 0 14px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const EffectiveDate = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

// ─── Content ──────────────────────────────────────────────────────────────────

const Content = styled.div`
  font-size: 1.02rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.72);

  p {
    margin: 0 0 18px;
  }

  a {
    color: #fe88f1;
    text-decoration: underline;
  }

  ul {
    margin: 0 0 18px;
    padding-left: 22px;

    li {
      margin-bottom: 6px;
      color: rgba(255, 255, 255, 0.65);
    }
  }
`;

const Section = styled.section`
  margin-bottom: 44px;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 14px;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(254, 136, 241, 0.3);
`;

const SubTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #fe88f1;
  margin: 20px 0 10px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin: 48px 0;
`;

const ContactBox = styled.div`
  padding: 20px 24px;
  background: rgba(254, 136, 241, 0.06);
  border: 1px solid rgba(254, 136, 241, 0.2);
  border-radius: 10px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.7;

  a {
    color: #fe88f1;
    text-decoration: underline;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const PrivacyPolicy = () => (
    <PageWrapper>
        <Article>
            <BackLink to="/">Back to Home</BackLink>

            <PageHeader>
                <CategoryBadge>Legal</CategoryBadge>
                <PageTitle>Privacy Policy</PageTitle>
                <EffectiveDate>Effective Date: February 26, 2026</EffectiveDate>
            </PageHeader>

            <Content>
                <Section>
                    <p>
                        MyCinemaDb ("we," "our," or "us") is a personal film discovery and movie analysis
                        website operated by Mariela Magallon.
                    </p>
                    <p>
                        This Privacy Policy explains how we collect, use, store, and protect information
                        when you use{' '}
                        <a href="https://mycinemadb.com" target="_blank" rel="noreferrer">
                            https://mycinemadb.com
                        </a>{' '}
                        (the "Website"), including when you log in using your Threads account.
                    </p>
                    <p>
                        By using this Website, you agree to the terms outlined in this Privacy Policy.
                    </p>
                </Section>

                <Section>
                    <SectionTitle>2. Information We Collect</SectionTitle>

                    <SubTitle>A. Information Collected Through Threads Login</SubTitle>
                    <p>
                        When you log in using your Threads account, we may collect the following
                        information from Meta (Threads):
                    </p>
                    <ul>
                        <li>Threads User ID</li>
                        <li>Username</li>
                        <li>Display name</li>
                        <li>Profile picture URL</li>
                    </ul>
                    <ul>
                        <li>We do not collect your Threads password.</li>
                        <li>We do not access private messages, contacts, or non-public Threads content.</li>
                    </ul>

                    <SubTitle>B. User-Generated Content</SubTitle>
                    <p>If you post comments on blog articles, we store:</p>
                    <ul>
                        <li>Your Threads username</li>
                        <li>Your profile picture (if provided by Threads)</li>
                        <li>The comment content you submit</li>
                        <li>Timestamp of the comment</li>
                    </ul>

                    <SubTitle>C. Technical Information</SubTitle>
                    <p>We may collect limited technical data automatically such as:</p>
                    <ul>
                        <li>IP address</li>
                        <li>Browser type</li>
                        <li>Device type</li>
                        <li>Basic usage data</li>
                    </ul>
                    <p>This data is used for security and website performance purposes.</p>
                </Section>

                <Section>
                    <SectionTitle>3. How We Use Your Information</SectionTitle>
                    <p>We use collected information to:</p>
                    <ul>
                        <li>Authenticate users via Threads</li>
                        <li>Display your username and profile picture with your comments</li>
                        <li>Maintain comment functionality</li>
                        <li>Improve website performance and user experience</li>
                        <li>Ensure security and prevent abuse</li>
                    </ul>
                    <p>We do not sell, rent, or trade your personal information.</p>
                </Section>

                <Section>
                    <SectionTitle>4. Legal Basis for Processing (EU Users)</SectionTitle>
                    <p>
                        If you are located in the European Union, we process your data based on:
                    </p>
                    <ul>
                        <li>Your consent (when logging in via Threads)</li>
                        <li>Legitimate interest (to operate and secure the website)</li>
                    </ul>
                </Section>

                <Section>
                    <SectionTitle>5. Data Storage and Processing Locations</SectionTitle>
                    <p>Your information may be processed and stored in:</p>
                    <ul>
                        <li>France (GoDaddy VPS hosting infrastructure)</li>
                        <li>United States (MongoDB Atlas database hosting in Virginia)</li>
                    </ul>
                    <p>
                        By using the Website, you acknowledge that your information may be transferred
                        across international borders.
                    </p>
                </Section>

                <Section>
                    <SectionTitle>6. Data Processors and Service Providers</SectionTitle>
                    <p>
                        We use trusted third-party service providers to operate the Website:
                    </p>
                    <ul>
                        <li>GoDaddy – Hosting infrastructure (France)</li>
                        <li>MongoDB Atlas – Database storage (United States)</li>
                        <li>Meta Platforms, Inc. (Threads) – Authentication services</li>
                    </ul>
                    <p>
                        These providers process data only to the extent necessary to provide their services.
                    </p>
                </Section>

                <Section>
                    <SectionTitle>7. Data Retention</SectionTitle>
                    <p>We retain user data:</p>
                    <ul>
                        <li>As long as your account remains active</li>
                        <li>As necessary to maintain comment history</li>
                        <li>As required for legal or security purposes</li>
                    </ul>
                    <p>You may request deletion of your data at any time (see Section 10).</p>
                </Section>

                <Section>
                    <SectionTitle>8. Data Security</SectionTitle>
                    <p>
                        We implement reasonable technical and organizational measures to protect user data,
                        including:
                    </p>
                    <ul>
                        <li>Secure server infrastructure</li>
                        <li>Access controls</li>
                        <li>Encrypted communication (HTTPS)</li>
                        <li>Limited administrative access</li>
                    </ul>
                    <p>
                        Only the website owner (Mariela Magallon) has administrative access to stored
                        user data.
                    </p>
                </Section>

                <Section>
                    <SectionTitle>9. Sharing of Information</SectionTitle>
                    <p>We do not share personal data with third parties except:</p>
                    <ul>
                        <li>With service providers listed above</li>
                        <li>When required by law</li>
                        <li>To comply with valid legal requests</li>
                    </ul>
                    <p>
                        We review any legal request for data and disclose only the minimum amount
                        required by law.
                    </p>
                </Section>

                <Section>
                    <SectionTitle>10. Your Rights</SectionTitle>
                    <p>Depending on your location, you may have the right to:</p>
                    <ul>
                        <li>Request access to your personal data</li>
                        <li>Request correction of inaccurate data</li>
                        <li>Request deletion of your data</li>
                        <li>Withdraw consent</li>
                    </ul>
                    <p>
                        To request data deletion or inquire about your data, contact:{' '}
                        <a href="mailto:admin@mycinemadb.com">admin@mycinemadb.com</a>
                    </p>
                    <p>
                        If you log in via Threads, you may also revoke access by removing the app
                        from your Threads settings.
                    </p>
                </Section>

                <Section>
                    <SectionTitle>11. Children's Privacy</SectionTitle>
                    <p>This Website is not directed toward children under 13.</p>
                    <p>We do not knowingly collect personal data from children.</p>
                </Section>

                <Section>
                    <SectionTitle>12. Changes to This Policy</SectionTitle>
                    <p>
                        We may update this Privacy Policy from time to time. Changes will be posted
                        on this page with an updated effective date.
                    </p>
                </Section>

                <Divider />

                <Section>
                    <SectionTitle>13. Contact Information</SectionTitle>
                    <ContactBox>
                        <strong style={{ color: '#fff' }}>Mariela Magallon</strong>
                        <br />
                        Email:{' '}
                        <a href="mailto:admin@mycinemadb.com">admin@mycinemadb.com</a>
                    </ContactBox>
                </Section>
            </Content>

            <BackLink to="/">Back to Home</BackLink>
        </Article>
    </PageWrapper>
);

export default PrivacyPolicy;
